import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowUpRight, Download, Gauge, CheckCircle2, Trophy, Mail } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import StrategyCallDialog from "@/components/StrategyCallDialog";
import PricingTiers from "@/components/PricingTiers";
import { FoundingBadge } from "@/components/FoundingBadge";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { PLAYBOOK_STEPS, PLAYBOOK_PDF_URL, PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";
import { useProgress } from "@/hooks/useProgress";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO =
  "https://images.pexels.com/photos/11087837/pexels-photo-11087837.jpeg";

const COVER =
  "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/1ebfe7a44dbb96c3440c559a6a7d67209cfdb6dee462259249c701080055deee.png";

export default function PlaybookHome() {
  const { isDone, count } = useProgress();
  const { user } = useAuth();
  const pct = Math.round((count / PLAYBOOK_STEPS.length) * 100);
  const location = useLocation();

  // Scroll to an in-page section when arriving with a hash (e.g., /#playbook).
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [location.hash]);

  useEffect(() => {
    if (pct === 100) {
      const lead_id = localStorage.getItem("routehaul_lead_id");
      const flagged = localStorage.getItem("routehaul_playbook_flagged");
      if (lead_id && !flagged) {
        axios
          .post(`${API}/leads/hot`, { lead_id })
          .then(() => localStorage.setItem("routehaul_playbook_flagged", "1"))
          .catch(() => {});
      }
    }
  }, [pct]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Cover */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="cover-section">
        <div className="absolute inset-0">
          <img src={COVER} alt="Modern semi truck on the open highway" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-brand mb-6">
              <span className="w-8 h-px bg-brand" /> {PROGRAM_NAME}
            </div>
            {user?.founding_member && <div className="mb-5"><FoundingBadge /></div>}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[0.98]">
              Ready to begin your journey to <span className="text-brand">running your own business?</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-700 max-w-xl">
              Starting your own trucking business isn't always easy — but you don't have to figure it out
              alone. {PROGRAM_NAME} guides you every step of the way, from your very first readiness check to
              hauling under your own authority. Take a 2-minute diagnostic, follow the proven 12-Step
              Business Foundation Guide, know your true cost per mile, and lean on a lane strategist when you're
              ready to roll.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/diagnostic" data-testid="cover-diagnostic-cta">
                <Button className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold group w-full sm:w-auto">
                  <Gauge className="w-5 h-5 mr-2" /> Take the free 2-min diagnostic
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#playbook" data-testid="cover-playbook-cta">
                <Button variant="outline" className="h-14 px-8 rounded-full border-slate-300 bg-white/70 backdrop-blur text-slate-900 text-base font-bold w-full sm:w-auto">
                  Explore the 12-Step Business Foundation Guide
                </Button>
              </a>
            </div>
            {user === false && (
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="cover-google-signup">
                <GoogleSignInButton testid="cover-google-signin" label="Sign up free with Google" className="w-full sm:w-auto" />
                <span className="text-sm text-slate-500">Free to start — no card needed.</span>
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-sm font-medium text-slate-600">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Free to start</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> AI-graded readiness score</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Built by a lane strategist</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero */}
      <section id="playbook" className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-6">
            <span className="w-8 h-px bg-brand" /> {PROGRAM_NAME}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[0.95]">
            The 12-Step Business Foundation Guide
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
            Buying a truck is the easy part. Building a compliant, profitable carrier that survives DOT
            regulations and insurance markets is the real work. Follow the 12 steps — each one links you
            straight to the official resources you need.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#step-01" data-testid="start-step-1-button">
              <Button className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold group">
                Start with Step 1
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href={PLAYBOOK_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="download-pdf-hero">
              <Button variant="outline" className="h-14 px-8 rounded-full border-slate-300 text-slate-900 text-base font-bold">
                <Download className="w-5 h-5 mr-2" /> Download PDF
              </Button>
            </a>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-brand rounded-md z-0" />
          <img src={HERO} alt="Semi truck on highway" className="relative z-10 w-full h-[420px] object-cover rounded-md border border-slate-200" />
        </motion.div>
      </section>

      {/* Steps grid */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          {pct === 100 && (
            <div data-testid="all-complete-banner" className="mb-12 rounded-md bg-emerald-600 text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-md bg-white/15 flex items-center justify-center shrink-0">
                  <Trophy className="w-7 h-7 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Congratulations — all 12 steps complete!</h2>
                  <p className="text-emerald-50 mt-1 max-w-xl">You've built the full foundation for a compliant, profitable carrier. There's just one thing left to do — launch.</p>
                </div>
              </div>
              <div className="shrink-0">
                <StrategyCallDialog source="strategy_playbook_complete" triggerClassName="h-14 px-8 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 text-base font-bold" />
              </div>
            </div>
          )}
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">The path to authority</div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
                12 steps. Tap any one to dive in.
              </h2>
            </div>
            <div className="w-full sm:w-72" data-testid="progress-widget">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Your progress</span>
                <span className="font-mono text-sm font-bold tabular text-slate-900" data-testid="progress-count">{count} / {PLAYBOOK_STEPS.length}</span>
              </div>
              <Progress value={pct} className="h-2 bg-slate-200 [&>div]:bg-brand" />
              <p className="text-xs text-slate-400 mt-2">{pct === 100 ? "All steps complete — you're authority-ready." : "Check off steps as you complete them. Progress saves automatically."}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLAYBOOK_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                id={`step-${String(s.n).padStart(2, "0")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={`/step/${s.n}`}
                  data-testid={`step-card-${s.n}`}
                  className={`group block h-full bg-white border rounded-md p-6 hover:border-brand hover:shadow-sm transition-[border-color,box-shadow] duration-200 ${
                    isDone(s.n) ? "border-emerald-300" : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400">{s.tag}</span>
                    {isDone(s.n) ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" data-testid={`step-done-${s.n}`} />
                    ) : (
                      <span className="font-mono text-2xl font-bold tabular text-slate-200 group-hover:text-brand transition-colors">
                        {String(s.n).padStart(2, "0")}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.text}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-brand">
                    Open step <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diagnostic CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Not sure where you stand?</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
              Run the 2-minute readiness diagnostic.
            </h2>
            <p className="mt-3 text-slate-300">
              Get an AI-graded score and find out whether to self-correct with the Guide or work with a lane strategist.
            </p>
          </div>
          <Link to="/diagnostic" data-testid="go-diagnostic-button">
            <Button className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold">
              <Gauge className="w-5 h-5 mr-2" /> Run my diagnostic
            </Button>
          </Link>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-slate-200 bg-slate-50" data-testid="landing-pricing">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Simple pricing</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Start free. Go Pro when you're ready.</h2>
            <p className="mt-4 text-slate-600">Learn the business at no cost. Add the tools that run the numbers when you're ready to put it to work.</p>
          </div>
          <PricingTiers />
        </div>
      </section>

      {/* Meet your strategist */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-20 h-20 bg-brand rounded-md z-0" />
              <div className="relative z-10 bg-slate-900 rounded-md p-8 text-white">
                <div className="w-16 h-16 rounded-full bg-brand flex items-center justify-center font-heading font-black text-2xl mb-5">LW</div>
                <h3 className="font-heading text-2xl font-black tracking-tight">{STRATEGIST.name}</h3>
                <p className="text-brand font-bold text-sm mt-1">{STRATEGIST.title}</p>
                <p className="text-slate-400 text-sm mt-0.5">{STRATEGIST.business}</p>
                <a href={`mailto:${STRATEGIST.email}`} data-testid="strategist-email-link" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-brand mt-4 break-all">
                  <Mail className="w-4 h-4 shrink-0" /> {STRATEGIST.email}
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Work with a lane strategist</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-slate-900 leading-tight">
              Turn busy miles into profitable ones.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{STRATEGIST.blurb}</p>
            <div className="mt-8">
              <StrategyCallDialog source="strategy_playbook" triggerClassName="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold" />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} {PROGRAM_NAME}</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-slate-900">Terms &amp; Conditions</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-slate-900">Privacy Policy</Link>
            <span className="font-mono hidden sm:inline">The 12-Step Business Foundation Guide</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
